// src/context/AuthContext.jsx
import React, { createContext, useState, useEffect, useContext } from 'react';
import authService from '../services/firebase/auth.service';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already logged in on mount
  useEffect(() => {
    const unsubscribe = authService.onAuthStateChange(async (firebaseUser) => {
      if (firebaseUser) {
        // User is signed in
        try {
          const data = await authService.getCurrentUserData();
          setUser(firebaseUser);
          setUserData(data);
        } catch (err) {
          console.error('Error fetching user data:', err);
          setUser(null);
          setUserData(null);
        }
      } else {
        // User is signed out
        setUser(null);
        setUserData(null);
      }
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  // Login function
  const login = async (email, password, rememberMe = false) => {
    try {
      setError(null);
      const result = await authService.login(email, password);
      
      if (result.success) {
        setUser(result.user);
        setUserData(result.userData);
        
        // Store remember me preference
        if (rememberMe) {
          localStorage.setItem('rememberMe', 'true');
          localStorage.setItem('userEmail', email);
        } else {
          localStorage.removeItem('rememberMe');
          localStorage.removeItem('userEmail');
        }
        
        return { success: true };
      } else {
        setError(result.message);
        return { success: false, message: result.message };
      }
    } catch (err) {
      const errorMessage = err.message || 'Login failed';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    }
  };

  // Register function
  const register = async (email, password, userData) => {
    try {
      setError(null);
      const result = await authService.register(email, password, userData);
      
      if (result.success) {
        setUser(result.user);
        // Fetch complete user data
        const data = await authService.getCurrentUserData();
        setUserData(data);
        return { success: true };
      } else {
        setError(result.message);
        return { success: false, message: result.message };
      }
    } catch (err) {
      const errorMessage = err.message || 'Registration failed';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      setUserData(null);
      setError(null);
      // Clear remember me if user logs out
      localStorage.removeItem('rememberMe');
      localStorage.removeItem('userEmail');
      return { success: true };
    } catch (err) {
      console.error('Logout error:', err);
      return { success: false, message: err.message };
    }
  };

  // Reset password function
  const resetPassword = async (email) => {
    try {
      setError(null);
      const result = await authService.resetPassword(email);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Password reset failed';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    }
  };

  // Check if user is admin
  const isAdmin = () => {
    return userData?.role === 'admin' || userData?.role === 'super-admin';
  };

  // Get remembered email
  const getRememberedEmail = () => {
    if (localStorage.getItem('rememberMe') === 'true') {
      return localStorage.getItem('userEmail') || '';
    }
    return '';
  };

  const value = {
    user,
    userData,
    loading,
    error,
    login,
    register,
    logout,
    resetPassword,
    isAdmin,
    getRememberedEmail,
    setError
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;